"use client";

import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { fetchSubscriptionStatus, clearSubscription } from "@/redux/slices/subscription.slice";
import Cookies from "js-cookie";
import { fetchPublicAuthConfig } from "@/services/publicAuthConfigService";

var POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

/**
 * SubscriptionProvider — background service that polls subscription status.
 *
 * Fetches on:
 *  1. Mount (if signed in)
 *  2. Auth state change (login/profile switch)
 *  3. Every 5 minutes while signed in
 *
 * Clears on logout.
 */
export default function SubscriptionProvider({ children }) {
    var dispatch = useAppDispatch();
    var authState = useAppSelector(function (state) { return state.Auth; });
    var intervalRef = useRef(null);
    var prevLoginRef = useRef(false);

    var isLoggedIn = !!(authState?.profile?.isLogin || authState?.isLogin);

    // Fetch on mount + start polling when logged in
    useEffect(function () {
        var isCancelled = false;

        var fetchAfterAuthConfig = function () {
            fetchPublicAuthConfig().then(function () {
                if (!isCancelled) {
                    dispatch(fetchSubscriptionStatus());
                }
            });
        };

        if (isLoggedIn) {
            fetchAfterAuthConfig();

            // Set up 5-minute polling
            intervalRef.current = setInterval(function () {
                var token = Cookies.get("userAccessToken");
                if (token) {
                    fetchAfterAuthConfig();
                }
            }, POLL_INTERVAL);
        } else {
            // User logged out — clear state and stop polling
            dispatch(clearSubscription());
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }

        return function () {
            isCancelled = true;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isLoggedIn, dispatch]);

    // Re-fetch when user logs in or switches profile
    useEffect(function () {
        var isCancelled = false;

        if (isLoggedIn && !prevLoginRef.current) {
            fetchPublicAuthConfig().then(function () {
                if (!isCancelled) {
                    dispatch(fetchSubscriptionStatus());
                }
            });
        }
        prevLoginRef.current = isLoggedIn;

        return function () {
            isCancelled = true;
        };
    }, [isLoggedIn, authState?.profile, dispatch]);

    return children;
}
